import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Checkbox, message, Card, Row, Col, Input } from "antd";
import { API_URL } from "../../url_config";
import {NavigationStepsKeolis} from "../../components/navbars/navbar_keolis"
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";

const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (zones texte : Commentaire + Aide) ===
function TablePoSielAd({ record, onChange, setIsUpdating }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = async (field, value) => {
    setIsUpdating(true);
    onChange(record.key, field, value);
    try {
      await axios.patch(
        `${API_URL}/validations/validation_po_siel_ad/${record.id}/`,
        { [field]: value }
      );
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    } finally {
      setTimeout(() => setIsUpdating(false), 4000);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={4}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Informations</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                (Supervision)
              </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={4}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationPoSielAdKeolis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Charger les données
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isUpdating) return;

      try {
        const res = await axios.get(
          `${API_URL}/validations/validation_po_siel_ad/?ot_tag=keolis`
        );
        if (!isMounted) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          po_siel: item.po_siel,
          presence_lignes_dsp: item.presence_lignes_dsp,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    setLoading(true);
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isUpdating]);

  // ✅ Mise à jour locale + serveur
  const handleDataChange = async (key, field, value) => {
    setIsUpdating(true);

    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    try {
      const updatedItem = data.find((item) => item.key === key);
      if (updatedItem) {
        await axios.patch(
          `${API_URL}/validations/validation_po_siel_ad/${updatedItem.id}/`,
          { [field]: value }
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    } finally {
      setTimeout(() => setIsUpdating(false), 2000);
    }
  };

  const columns = [
    {
      title: "PO SIEL",
      dataIndex: "po_siel",
      key: "po_siel",
    },
    {
      title: "Présence lignes DSP",
      dataIndex: "presence_lignes_dsp",
      key: "presence_lignes_dsp",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_lignes_dsp", e.target.checked)
          }
        />
      ),
    },
    {
  title: "ETAT",
  dataIndex: "signal",
  render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
  },
},
  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsKeolis selectedKey="po_siel_ad" />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TablePoSielAd
              record={record}
              onChange={handleDataChange}
              setIsUpdating={setIsUpdating}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />


      <div style={{ padding: 20 }}>
            <ModifyItemPage
              apiUrl={`${API_URL}/validations/validation_po_siel_ad/`}
              ot_tag="keolis"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                { title: "PO Siel", dataIndex: "po_siel", key: "po_siel" },
              ]}
              formFields={[
                { name: "po_siel", label: "PO Siel", placeholder: "Entrez PO Siel" },
              ]}
            />
      </div>


    </div>
  );
};

export default ValidationPoSielAdKeolis;
